import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, PhoneCall } from "lucide-react";
import PublicChatWidget from "@/components/PublicChatWidget";
import api from "@/lib/api";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent,
} from "@/lib/hybridTheme";

const TIMELINE = [
  {
    num: "I.", day: "Day 1", title: "Discovery call",
    p: "A 20-minute call where we map out exactly how your phone currently works — hours, services, pricing, and the calls you actually want booked.",
    bullets: [
      "Your services, pricing, and scheduling rules",
      "Which calls should book automatically vs. route to you",
      "Your current calendar and CRM setup, if any",
      "What 'urgent' means for your business",
    ],
  },
  {
    num: "II.", day: "Days 2–4", title: "We configure the agent",
    p: "This is entirely on us. We write the call scripts, connect your calendar, and set up the follow-up sequences based on what you told us.",
    bullets: [
      "Voice agent trained on your specific services and FAQs",
      "Calendar integration (Google Calendar, Calendly, or your existing tool)",
      "Text follow-up sequences written and connected",
      "Call routing rules set for urgent vs. routine calls",
    ],
  },
  {
    num: "III.", day: "Day 5", title: "You test it live",
    p: "We send you the number. You call it, try to break it, ask it weird questions — and we adjust anything that doesn't sound right before a real customer ever hears it.",
    bullets: [
      "You test real call scenarios from your own phone",
      "We adjust scripts, tone, or routing based on your feedback",
      "A second test round if anything needs changing",
      "Sign-off from you before going live — no surprises",
    ],
  },
  {
    num: "IV.", day: "Days 6–7", title: "Go live", done: true,
    p: "We forward your existing number (or set up a new one) and the agent starts taking real calls. You get a weekly summary so you always know what it's handling.",
    bullets: [
      "Your existing number forwarded — no number change required",
      "Real calls start being answered and booked automatically",
      "Weekly summary report sent to you every Monday",
      "Direct line to us for any adjustments after launch",
    ],
  },
];

export default function OnboardingPublic() {
  const [agent, setAgent] = useState(null);
  useEffect(() => {
    api.get("/public/site-config").then(({ data }) => setAgent(data.agent || null)).catch(() => {});
  }, []);

  return (
    <PageShell testId="onboarding-public-page">
      <TopNav activeKey="onboarding" />

      <section style={{ padding: "112px 0 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, right: -100, width: 600, height: 600, background: "radial-gradient(circle, rgba(229,83,59,0.08) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={container()}>
          <div style={{ maxWidth: 900, position: "relative" }}>
            <div style={eyebrow()}>Onboarding</div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(52px, 6.5vw, 96px)", lineHeight: 0.98, letterSpacing: "-0.035em", fontWeight: 400, margin: "16px 0 24px", color: T.ink }}>
              From <em style={italicAccent(T.brass)}>signed</em><br />
              to <em style={italicAccent(T.coral)}>live</em> in a week.
            </h1>
            <p style={{ fontSize: 20, lineHeight: 1.55, color: T.inkMuted, maxWidth: 640, margin: 0 }}>
              We do 90% of the work. You spend about 45 minutes total. Your AI concierge answers real customer calls by Friday.
            </p>
          </div>
        </div>
      </section>

      <section style={{ padding: "56px 0 96px" }}>
        <div style={container()}>
          <div style={{ display: "grid", gap: 24 }}>
            {TIMELINE.map((step) => (
              <div key={step.num} data-testid={`onboarding-step-${step.num.replace('.','')}`} style={{
                background: step.done ? T.ink : T.panel,
                color: step.done ? T.cream : T.ink,
                border: `1px solid ${step.done ? T.ink : T.line}`,
                padding: "36px 40px",
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 40,
                alignItems: "start",
              }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 68, fontStyle: "italic", fontWeight: 400, color: step.done ? T.brass : T.coral, lineHeight: 1 }}>
                    {step.num}
                  </div>
                  <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: step.done ? "rgba(10,14,26,0.6)" : T.inkFaint, fontWeight: 600, marginTop: 12 }}>
                    {step.day}
                  </div>
                </div>
                <div>
                  <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: 500, letterSpacing: "-0.015em", margin: 0, color: step.done ? T.cream : T.ink }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: 15, lineHeight: 1.65, color: step.done ? "rgba(10,14,26,0.72)" : T.inkMuted, marginTop: 12 }}>
                    {step.p}
                  </p>
                  <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {step.bullets.map((b) => (
                      <li key={b} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, lineHeight: 1.55, color: step.done ? "rgba(10,14,26,0.85)" : T.inkSoft }}>
                        <Check size={15} strokeWidth={2.5} style={{ marginTop: 2, color: step.done ? "#059669" : T.coral, flexShrink: 0 }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "72px 0 120px" }}>
        <div style={container()}>
          <div style={{ background: T.ink, color: T.cream, padding: "56px 48px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, background: `radial-gradient(circle, ${T.coral} 0%, transparent 65%)`, opacity: 0.55, borderRadius: "50%" }} />
            <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 40, alignItems: "center" }}>
              <div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 48, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, margin: 0 }}>
                  Ready to be <em style={italicAccent(T.coral)}>live in a week?</em>
                </h3>
                <p style={{ marginTop: 20, maxWidth: 460, lineHeight: 1.6, opacity: 0.75 }}>
                  Book a discovery call. If we're a fit, you can be answering calls with AI by Friday.
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Link to="/#contact" style={{ background: T.coral, color: T.cream, border: "none", padding: "16px 24px", fontSize: 15, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  Book discovery call <ArrowUpRight size={16} />
                </Link>
                <Link to="/pricing-public" style={{ background: "transparent", color: T.cream, border: `1px solid rgba(247,244,238,0.3)`, padding: "16px 24px", fontSize: 15, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <PhoneCall size={15} /> See pricing first
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <PublicChatWidget agent={agent} />
    </PageShell>
  );
}
